import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";

/**
 * ReliabilityTrendChart — three-line daily view of the reliability
 * metrics that don't show up on the Coverage % trend:
 *
 *   Tardy %          — teal   — check-ins > 15min past shift start
 *   Early Clock-out %— violet — check-outs > 15min before shift end
 *   No-Show count    — red    — past shifts with no check-in
 *
 * Definitions match the aggregate `/metrics/operational` KPIs so the
 * line values line up with the KPI tiles above the chart. Backend
 * source: `/metrics/operational/timeseries` (iter309 extension).
 *
 * The two rates share the left Y-axis (0-100%). No-show is a raw
 * count, plotted on the right axis so its scale doesn't get squashed
 * by the two percentages.
 */
export default function ReliabilityTrendChart({ start, end, siteId, cadenceLabel, refreshTick = 0 }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { start, end };
        if (siteId) params.site_id = siteId;
        const r = await api.get("/metrics/operational/timeseries", { params });
        if (!cancelled) setSeries(r.data?.series || []);
      } catch {
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [start, end, siteId, refreshTick]);

  const chartData = useMemo(
    () => series.map((s) => ({
      ...s,
      label: (() => {
        const d = new Date(s.date + "T00:00:00Z");
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      })(),
      // Backend renamed early_clock_out_pct — keep raw as-is; recharts
      // uses whichever field name we hand it.
    })),
    [series]
  );

  // Summary chips: window totals + a "worst day" pointer for each
  // metric. Helps ops jump straight to the day that dragged the week
  // down without hunting through the chart.
  const summary = useMemo(() => {
    if (chartData.length === 0) return null;
    const withShifts = chartData.filter((r) => r.shifts_considered > 0);
    const worst = (getter) =>
      withShifts.reduce((best, r) =>
        (getter(r) > getter(best || { [getter.name]: -1, date: "" }) ? r : best), null);
    const avg = (getter) => withShifts.length
      ? +(withShifts.reduce((a, r) => a + getter(r), 0) / withShifts.length).toFixed(1)
      : 0;
    const totalNoShow = chartData.reduce((a, r) => a + (r.no_show_shifts || 0), 0);
    const worstNoShow = chartData.reduce(
      (best, r) => ((r.no_show_shifts || 0) > (best?.no_show_shifts || 0) ? r : best),
      null,
    );
    return {
      tardyAvg: avg((r) => r.tardy_pct),
      tardyWorst: worst((r) => r.tardy_pct),
      earlyAvg: avg((r) => r.early_clock_out_pct),
      earlyWorst: worst((r) => r.early_clock_out_pct),
      noShowTotal: totalNoShow,
      noShowWorst: worstNoShow,
    };
  }, [chartData]);

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-5" data-testid="reliability-trend-chart">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <h3 className="font-display uppercase font-bold text-lg tracking-tight text-zinc-100">
            Reliability Trend
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Tardy % (teal) · Early Clock-out % (violet) · No-shows (red bars)
            {siteId ? " · site-scoped" : ""} · <span className="text-zinc-300">{cadenceLabel}</span>
          </p>
        </div>
        {summary && (
          <div className="flex items-center gap-3 flex-wrap">
            <SummaryChip label="Tardy avg" value={`${summary.tardyAvg}%`}
              tone="#0FBFCB"
              hint={summary.tardyWorst ? `worst ${summary.tardyWorst.tardy_pct}% on ${summary.tardyWorst.date.slice(5)}` : null}
              testId="reliability-tardy-summary" />
            <SummaryChip label="Early avg" value={`${summary.earlyAvg}%`}
              tone="#A78BFA"
              hint={summary.earlyWorst ? `worst ${summary.earlyWorst.early_clock_out_pct}% on ${summary.earlyWorst.date.slice(5)}` : null}
              testId="reliability-early-summary" />
            <SummaryChip label="No-shows" value={summary.noShowTotal}
              tone="#FF3B30"
              hint={summary.noShowWorst?.no_show_shifts ? `${summary.noShowWorst.no_show_shifts} on ${summary.noShowWorst.date.slice(5)}` : null}
              testId="reliability-no-show-summary" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-xs text-zinc-500">Loading trend…</div>
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-xs text-zinc-500">
          No shifts in this window.
        </div>
      ) : (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#a1a1aa", fontSize: 10 }}
                stroke="#3F3F46"
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="pct"
                tick={{ fill: "#a1a1aa", fontSize: 10 }}
                stroke="#3F3F46"
                domain={[0, (dataMax) => Math.max(20, Math.ceil((dataMax || 20) / 10) * 10)]}
                tickFormatter={(v) => `${v}%`}
              />
              <YAxis
                yAxisId="count"
                orientation="right"
                tick={{ fill: "#a1a1aa", fontSize: 10 }}
                stroke="#3F3F46"
                allowDecimals={false}
                domain={[0, (dataMax) => Math.max(3, Math.ceil((dataMax || 3) + 1))]}
              />
              <Tooltip
                contentStyle={{
                  background: "#0A0A0A",
                  border: "1px solid #27272A",
                  fontSize: 11,
                }}
                labelStyle={{ color: "#a1a1aa", fontWeight: 700, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.1em" }}
                itemStyle={{ color: "#e4e4e7" }}
                formatter={(value, name, ctx) => {
                  const row = ctx.payload;
                  if (name === "tardy_pct") return [`${value}% (${row?.tardy_shifts}/${row?.shifts_considered})`, "Tardy"];
                  if (name === "early_clock_out_pct") return [`${value}% (${row?.early_out_shifts}/${row?.shifts_considered})`, "Early clock-out"];
                  if (name === "no_show_shifts") return [`${value} shifts`, "No-shows"];
                  return [value, name];
                }}
              />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "#a1a1aa" }}
                formatter={(v) => {
                  if (v === "tardy_pct") return "Tardy %";
                  if (v === "early_clock_out_pct") return "Early Clock-out %";
                  if (v === "no_show_shifts") return "No-Show count";
                  return v;
                }}
              />
              <ReferenceLine
                yAxisId="pct" y={10} stroke="#FF9F0A" strokeDasharray="4 4" strokeOpacity={0.4}
                label={{ value: "10% target", fill: "#FF9F0A", fontSize: 9, position: "insideLeft" }}
              />
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="tardy_pct"
                stroke="#0FBFCB"
                strokeWidth={2}
                dot={{ r: 3, fill: "#0FBFCB", stroke: "#0FBFCB" }}
                activeDot={{ r: 5 }}
                isAnimationActive
              />
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="early_clock_out_pct"
                stroke="#A78BFA"
                strokeWidth={2}
                dot={{ r: 3, fill: "#A78BFA", stroke: "#A78BFA" }}
                activeDot={{ r: 5 }}
                isAnimationActive
              />
              <Line
                yAxisId="count"
                type="stepAfter"
                dataKey="no_show_shifts"
                stroke="#FF3B30"
                strokeWidth={2}
                dot={{ r: 3, fill: "#FF3B30", stroke: "#FF3B30" }}
                activeDot={{ r: 5 }}
                isAnimationActive
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function SummaryChip({ label, value, hint, tone, testId }) {
  return (
    <div className="text-right" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-xl leading-none" style={{ color: tone }}>{value}</div>
      {hint && <div className="text-[9px] text-zinc-600 mt-1 font-mono">{hint}</div>}
    </div>
  );
}
